import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-cost-per-kwh-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './cost-per-kwh-chart.component.html',
  styleUrl: './cost-per-kwh-chart.component.css'
})
export class CostPerKwhChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  //marketAverage: number = 0.35;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummytransparentBaseline: number[] = [0, 0.43, 0.47, 0.49, 0.53, 0];
  dummystepsData: number[] = [0.43, 0.04, 0.02, 0.06, 0.04, 0.59];

  baselineLabel: string = "Baseline";
  costPerKwhLabel: string = "Cost per kWh";

  stepsLabel: string[] = ['Previous Period', 'Material Costs', 'TMS', 'Taxes', 'Additional Charges', 'Currnet Period'];
  numberOfSteps: number = 5;

  transparentBaseline: number[] = [0, 0.43, 0.47, 0.49, 0.53, 0];
  stepsData: number[] = [0.43, 0.04, 0.02, 0.06, 0.04, 0.59];

  @Input() materialCostsDifferenceSign: string = '+';
  @Input() materialCostsDifference: number = 0;

  @Input() tmsDifferenceSign: string = '+';
  @Input() tmsDifference: number = 0;

  @Input() taxesDifferenceSign: string = '+';
  @Input() taxesDifference: number = 0;

  @Input() additionalChargesDifferenceSign: string = '+';
  @Input() additionalChargesDifference: number = 0;

  @Input() previousPeriodCostPerKwh: number = 0;
  @Input() currentPeriodCostPerKwh: number = 0;

  @Input() inputedPeriods: string[] | undefined = undefined;

  prevPeriod: string = 'Previous Period';
  currPeriod: string = 'Currnet Period';

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();
    combineLatest([
      this.translocoService.selectTranslate('dashboard.baseline'),
      this.translocoService.selectTranslate('dashboard.cost_per_kwh')
    ]).subscribe(([baselineTranslation, costPerKwhTranslation]) => {
      this.baselineLabel = baselineTranslation;
      this.costPerKwhLabel = costPerKwhTranslation;
    });

    combineLatest([
      this.translocoService.selectTranslate('dashboard.period.previous'),
      this.translocoService.selectTranslate('dashboard.cost_comparison_labels.material_costs'),
      this.translocoService.selectTranslate('dashboard.cost_comparison_labels.tms'),
      this.translocoService.selectTranslate('dashboard.cost_comparison_labels.taxes'),
      this.translocoService.selectTranslate('dashboard.cost_comparison_labels.additional_charges'),
      this.translocoService.selectTranslate('dashboard.period.current')
    ]).subscribe(([prevPeriod, energyTranslation, tmsTranslation, taxesTranslation, additionalChargesTranslation, currPeriod]) => {
      this.prevPeriod = prevPeriod;
      this.currPeriod = currPeriod;

      const previous = this.inputedPeriods?.[0] || prevPeriod;
      const current = this.inputedPeriods?.[1] || currPeriod;

      this.stepsLabel = [previous, energyTranslation, tmsTranslation, taxesTranslation, additionalChargesTranslation, current];
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.transparentBaseline = this.dummytransparentBaseline;
      this.stepsData = this.dummystepsData;
    }
    else {
      this.transparentBaseline = [0, this.previousPeriodCostPerKwh, (this.previousPeriodCostPerKwh + this.materialCostsDifference), (this.previousPeriodCostPerKwh + this.materialCostsDifference + this.tmsDifference), (this.previousPeriodCostPerKwh + this.materialCostsDifference + this.tmsDifference + this.taxesDifference), 0]
      this.stepsData = [this.previousPeriodCostPerKwh, this.materialCostsDifference, this.tmsDifference, this.taxesDifference, this.additionalChargesDifference, this.currentPeriodCostPerKwh]

      this.stepsLabel[0] = this.inputedPeriods?.[0] || this.prevPeriod;
      this.stepsLabel[5] = this.inputedPeriods?.[1] || this.currPeriod;
    }

    Chart.register(ChartDataLabels, AnnotationPlugin);

    this.data = {
      labels: this.stepsLabel,
      datasets: [
        {
          type: 'bar',
          label: this.baselineLabel,
          data: this.transparentBaseline,
          backgroundColor: 'transparent',
          borderWidth: 0,
          stack: 'combined',
          datalabels: {
            display: false,
          },
        },
        {
          type: 'bar',
          label: this.costPerKwhLabel,
          data: this.stepsData,
          backgroundColor: ['#D4DFC9', '#D4DFC9', '#D4DFC9', '#D4DFC9', '#D4DFC9', '#A9BE92'],
          borderRadius: 12,
          borderWidth: 1,
          datalabels: {
            display: true,
            align: 'center',
            anchor: 'center',
            color: '#000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            formatter: (value: number, context: any) => {
              const formatDifference = (sign: string, value: number) => {
                return value === 0 ? '' : `${sign} ${this.decimalPipe.transform(value, '1.2-2') || value}€`;
              };

              const diffs = [
                this.previousPeriodCostPerKwh === 0 ? '' : this.previousPeriodCostPerKwh.toFixed(2),
                formatDifference(this.materialCostsDifferenceSign, this.materialCostsDifference),
                formatDifference(this.tmsDifferenceSign, this.tmsDifference),
                formatDifference(this.taxesDifferenceSign, this.taxesDifference),
                formatDifference(this.additionalChargesDifferenceSign, this.additionalChargesDifference),
                this.currentPeriodCostPerKwh === 0 ? '' : this.currentPeriodCostPerKwh.toFixed(2),
              ];
              return diffs[context.dataIndex];
            },
          },
          stack: 'combined'
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        /*annotation: {
          annotations: {
            marketAverage: {
              type: 'line',
              yMin: this.marketAverage,
              yMax: this.marketAverage,
              xMin: 3.5,
              borderColor: '#FFA500',
              borderDash: [5, 5],
              borderWidth: 2,

            },
          },
        },*/
        tooltip: {
          callbacks: {
            label: (tooltipItem: any) => {
              const index = tooltipItem.dataIndex;

              const formatDifference = (sign: string, value: number) => {
                return value === 0 ? '' : `${sign} ${this.decimalPipe.transform(value, '1.2-2') || value}€`;
              };

              const diffs = [
                this.previousPeriodCostPerKwh === 0 ? '' : this.decimalPipe.transform(this.previousPeriodCostPerKwh, '1.2-2') || this.previousPeriodCostPerKwh,
                formatDifference(this.materialCostsDifferenceSign, this.materialCostsDifference),
                formatDifference(this.tmsDifferenceSign, this.tmsDifference),
                formatDifference(this.taxesDifferenceSign, this.taxesDifference),
                formatDifference(this.additionalChargesDifferenceSign, this.additionalChargesDifference),
                this.currentPeriodCostPerKwh === 0 ? '' : this.decimalPipe.transform(this.currentPeriodCostPerKwh, '1.2-2') || this.currentPeriodCostPerKwh
              ];

              return diffs[index] ? `${this.stepsLabel[index]}: ${diffs[index]}` : '';
            },
          },
        },

      },
      scales: {
        x: {
          stacked: true,
          title: {
            display: false,
          },
          ticks: {
            display: true,
          },
        },
        y: {
          beginAtZero: true,
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
      },
    };
  }
}
